import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup, Checkbox } from '../../../components/workbook/forms';

interface PriorityItem {
  id: string;
  title: string;
  outcome: string;
  owner: string;
  dueDate: string;
  value: string;
  timeThisWeek: string;
  suspect: boolean;
}

export default function PriorityInventoryPage() {
  const [items, setItems] = useState<PriorityItem[]>([
    { id: '1', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
    { id: '2', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
    { id: '3', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
    { id: '4', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
    { id: '5', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
    { id: '6', title: '', outcome: '', owner: '', dueDate: '', value: '', timeThisWeek: '', suspect: false },
  ]);

  const updateItem = (id: string, field: keyof PriorityItem, value: string | boolean) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addItem = () => {
    const newId = (items.length + 1).toString();
    setItems([...items, { 
      id: newId, 
      title: '', 
      outcome: '', 
      owner: '', 
      dueDate: '', 
      value: '', 
      timeThisWeek: '', 
      suspect: false 
    }]);
  };

  return (
    <TaskPage
      phase="decide"
      title="Priority Inventory"
      pageNumber={2}
      instruction="List every active commitment—projects, streams, recurring tasks. Be messy; capture it all first, judge later."
      whyItMatters="Outcome ≠ activity; it's the result. Owner = accountable person, not helper."
      coachRail={{
        timing: "10 minutes",
        tips: "Do silently first, then compare. Differences spotlight assumptions.",
        debrief: "What pattern did the inventory reveal?"
      }}
    >
      <div className="space-y-4">
        {/* Table Header */}
        <div className="grid grid-cols-7 gap-4 p-3 bg-[var(--hover-tint)] font-semibold text-sm">
          <div>Title</div>
          <div>Outcome</div>
          <div>Owner</div>
          <div>Due/Review</div>
          <div>Value</div>
          <div>Time this week</div>
          <div>Suspect?</div>
        </div>

        {/* Table Rows */}
        {items.map((item) => (
          <div key={item.id} className="grid grid-cols-7 gap-4 p-3 border border-[var(--rule)]">
            <TextInput
              value={item.title}
              onChange={(e) => updateItem(item.id, 'title', e.target.value)}
              placeholder="Project or task name"
              maxLength={80}
            />
            <TextInput
              value={item.outcome}
              onChange={(e) => updateItem(item.id, 'outcome', e.target.value)}
              placeholder="Result if successful"
              maxLength={120}
            />
            <TextInput
              value={item.owner}
              onChange={(e) => updateItem(item.id, 'owner', e.target.value)}
              placeholder="Accountable person"
              maxLength={40}
            />
            <input
              type="date"
              value={item.dueDate}
              onChange={(e) => updateItem(item.id, 'dueDate', e.target.value)}
              className="px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <RadioGroup
              options={[
                { value: 'H', label: 'H' },
                { value: 'M', label: 'M' },
                { value: 'L', label: 'L' }
              ]}
              value={item.value}
              onChange={(value) => updateItem(item.id, 'value', value)}
            />
            <RadioGroup
              options={[
                { value: '0-30m', label: '0-30m' },
                { value: '30-120m', label: '30-120m' },
                { value: '2h+', label: '2h+' }
              ]}
              value={item.timeThisWeek}
              onChange={(value) => updateItem(item.id, 'timeThisWeek', value)}
            />
            <Checkbox
              checked={item.suspect}
              onChange={(e) => updateItem(item.id, 'suspect', e.target.checked)}
              label=""
            />
          </div>
        ))}

        {/* Add Row Button */}
        <button
          onClick={addItem}
          className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
        >
          + Add another item
        </button>

        {/* Help Text */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <h4 className="text-sm font-semibold text-[var(--text-sec)] mb-2">Notes</h4>
          <ul className="text-sm text-[var(--text-sec)] space-y-1">
            <li>• Outcome ≠ activity; it's the result</li>
            <li>• Owner = accountable person, not helper</li>
            <li>• Time this week: 0-30m / 30-120m / 2h+</li>
          </ul>
        </div>
      </div>
    </TaskPage>
  );
}






